import React from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { SeoLockedFaqsNotice, StyledSeoNote } from '../../../SeoLockedContent';
import { StyledSectionTitle } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { ClinicWithConditionsAndPromo } from '../../../types';
import TextEditor from '../../../../FormEditors/TextEditor';
import HtmlEditor from '../../../../FormEditors/HtmlEditor';
import ImageEditor from '../../../../FormEditors/ImageEditor';
import ColorEditor from '../../../../FormEditors/ColorEditor';
import RepeatableRowsEditor from '../../../../FormEditors/RepeatableRowsEditor';

type ClinicConditionPromoEditorProps = Readonly<{
  form: UseFormReturn<ClinicWithConditionsAndPromo>;
  index: number;
  isDisabled?: boolean;
}>;

// Dynamic per-row field paths (from RepeatableRowsEditor) can't be statically inferred; cast them.
type ClinicFieldName = FieldPath<ClinicWithConditionsAndPromo>;

function ClinicConditionPromoEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <>
      <StyledSectionTitle>Promotional Content</StyledSectionTitle>
      <PromoScreenEditor {...props} />
      <ShopCardEditor {...props} />
      <HeroFieldsEditor {...props} />
      <HeroBenefitsEditor {...props} />
      <HowItWorksEditor {...props} />
      <FaqsEditor {...props} />
      <CtaBannerEditor {...props} />
    </>
  );
}

function HeroFieldsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Hero</StyledSectionTitle>
      <TextEditor
        label="Hero title"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.hero_title`}
        disabled={isDisabled}
      />
      <StyledSeoNote>
        🔒 Partner pharmacy websites replace this title with the generated SEO H1 (&quot;[Condition] Treatment in
        [town]&quot;). This title is used on the Healthera site.
      </StyledSeoNote>
      <TextEditor
        label="Hero subtitle"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.subtitle`}
        disabled={isDisabled}
      />
      <StyledSeoNote>
        🔒 Partner pharmacy websites append a generated collection/delivery sentence after this subtitle.
      </StyledSeoNote>
    </ColumnWithGap>
  );
}

function HeroBenefitsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Hero benefits (override)</StyledSectionTitle>
      <StyledSeoNote>
        Overrides the condition hero only; the green benefit bar always shows the clinic-level benefits.
      </StyledSeoNote>
      <StyledSeoNote>
        🔒 On partner pharmacy websites the THIRD benefit is replaced by a generated collection/delivery
        benefit (appended when fewer than three are authored); other benefits render as authored.
      </StyledSeoNote>
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.hero_benefits`}
        label="Benefits"
        addButtonLabel="Add benefit"
        emptyMessage="Leave empty to use the clinic-level benefits"
        disabled={isDisabled}
        createEmptyRow={() => ({ icon_url: '', text: '' })}
        renderRow={(rowName) => (
          <>
            <ImageEditor
              control={form.control}
              name={`${rowName}.icon_url` as ClinicFieldName}
              label="Icon"
              suggestedSize="60x60px"
              height="60px"
              width="60px"
            />
            <TextEditor
              label="Text"
              control={form.control}
              name={`${rowName}.text` as ClinicFieldName}
              disabled={isDisabled}
            />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function HowItWorksEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>How it works</StyledSectionTitle>
      <StyledSeoNote>
        🔒 On partner pharmacy websites the LAST step&apos;s description is replaced by a generated
        collection/delivery sentence; earlier steps render as authored.
      </StyledSeoNote>
      <TextEditor
        label="Section title"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.title`}
        optional
        disabled={isDisabled}
      />
      <TextEditor
        label="Section subtitle"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.subtitle`}
        optional
        disabled={isDisabled}
      />
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.steps`}
        label="Steps"
        addButtonLabel="Add step"
        disabled={isDisabled}
        createEmptyRow={() => ({ title: '', description: '', time_label: '', icon_url: '' })}
        renderRow={(rowName) => (
          <>
            <TextEditor label="Step title" control={form.control} name={`${rowName}.title` as ClinicFieldName} disabled={isDisabled} />
            <TextEditor label="Step description" control={form.control} name={`${rowName}.description` as ClinicFieldName} disabled={isDisabled} />
            <TextEditor label="Time label" control={form.control} name={`${rowName}.time_label` as ClinicFieldName} optional disabled={isDisabled} />
            <ImageEditor
              control={form.control}
              name={`${rowName}.icon_url` as ClinicFieldName}
              label="Step icon"
              suggestedSize="60x60px"
              height="60px"
              width="60px"
              disabled={isDisabled}
            />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function FaqsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>FAQs</StyledSectionTitle>
      <SeoLockedFaqsNotice page="condition" />
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.faqs`}
        label="Questions"
        addButtonLabel="Add FAQ"
        disabled={isDisabled}
        createEmptyRow={() => ({ question: '', answer: '' })}
        renderRow={(rowName) => (
          <>
            <TextEditor
              label="Question"
              control={form.control}
              name={`${rowName}.question` as ClinicFieldName}
              disabled={isDisabled}
            />
            <HtmlEditor label="Answer" control={form.control} name={`${rowName}.answer` as ClinicFieldName} />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function CtaBannerEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>CTA banner</StyledSectionTitle>
      <ImageEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.image_url`}
        label="Banner image"
        suggestedSize="1125x630px"
        height="210px"
        width="375px"
      />
      <TextEditor
        label="Heading"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.heading`}
        disabled={isDisabled}
      />
      <TextEditor
        label="Subtitle"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.subtitle`}
        disabled={isDisabled}
      />
      <TextEditor
        label="Button label"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.button.label`}
        disabled={isDisabled}
      />
    </ColumnWithGap>
  );
}

function PromoScreenEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Promo screen</StyledSectionTitle>
      <PromoTitleEditor {...props} />
      <PromoDescriptionEditor {...props} />
      <PromoDisclaimerEditor {...props} />
      <PromoImageEditor {...props} />
      <PromoColorEditor {...props} />
    </ColumnWithGap>
  );
}

function PromoTitleEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Promo title"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.title`}
      disabled={isDisabled}
    />
  );
}

function PromoDescriptionEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Description"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.description`}
      disabled={isDisabled}
    />
  );
}

function PromoDisclaimerEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Disclaimer"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.disclaimer`}
      disabled={isDisabled}
    />
  );
}

function PromoImageEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ImageEditor
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.image_url`}
      suggestedSize="1125x630"
      label="Promo screen image"
      height="210px"
      width="375px"
      disabled={isDisabled}
    />
  );
}

function PromoColorEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColorEditor
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.background_color`}
      label="Background color (#hex)"
      disabled={isDisabled}
    />
  );
}

function ShopCardEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Shop card promotion</StyledSectionTitle>
      <ShopCardTitleEditor {...props} />
      <ShopCardTaglineEditor {...props} />
      <ShopCardDescription {...props} />
      <ShopCardImage {...props} />
    </ColumnWithGap>
  );
}

function ShopCardTitleEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Shop card title"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.title`}
      disabled={isDisabled}
    />
  );
}

function ShopCardTaglineEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Shop card tagline"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.tagline`}
      optional
      disabled={isDisabled}
    />
  );
}

function ShopCardImage({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ImageEditor
      control={form.control}
      label="Shop card image"
      name={`clinic_conditions.${index}.promotional_content.shop_card.image_url`}
      suggestedSize="444x249"
      height="125px"
      width="222px"
      disabled={isDisabled}
    />
  );
}

function ShopCardDescription({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Shop card description"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.description`}
      disabled={isDisabled}
    />
  );
}

export default ClinicConditionPromoEditor;
